import { useState } from "react"
import { Screen } from "./App"
import {
  ApiError,
  fitnessOnDemandSaveLink,
  gympassActivate,
  login,
  promoCodeRedeem,
  promoLinkRedeem,
} from "./api"
import { useT } from "@transifex/react"

export const PasswordLoginScreen = ({
  email,
  promoCode,
  gympassToken,
  fitnessOnDemandToken,
  promoLinkToken,
  onComplete,
  onFailure,
}: {
  email: string
  promoCode: string | undefined
  gympassToken: string | undefined
  fitnessOnDemandToken: string | undefined
  promoLinkToken: string | undefined
  onComplete: (nextScreen: Screen, rewardDuration?: string) => void
  onFailure: (nextScreen: Screen, errorMessage?: string) => void
}) => {
  const t = useT()
  const [password, setPassword] = useState("")
  const [inputError, setInputError] = useState<string>()
  const [apiError, setApiError] = useState<string>()
  const [loading, setLoading] = useState(false)

  const loginAndRedeem = async () => {
    setApiError(undefined)
    if (password.length === 0) {
      setInputError(t("Password cannot be empty"))
      return
    }
    setLoading(true)
    try {
      const loggedIn = await login(email, password)
      if (loggedIn) {
        if (promoCode) {
          const rewardDuration = await promoCodeRedeem(promoCode)
          onComplete(Screen.SuccessExisting, rewardDuration)
        } else if (gympassToken) {
          await gympassActivate(gympassToken)
          onComplete(Screen.SuccessExisting)
        } else if (fitnessOnDemandToken) {
          await fitnessOnDemandSaveLink(fitnessOnDemandToken)
          onComplete(Screen.SuccessExisting)
        } else if (promoLinkToken) {
          await promoLinkRedeem(promoLinkToken)
          onComplete(Screen.SuccessExisting)
        } else {
          throw new Error("Bug: pass a token")
        }
      } else {
        setInputError(t("Invalid password"))
      }
    } catch (error) {
      if (error instanceof ApiError) {
        onFailure(Screen.Failed, error.message)
      } else {
        setApiError(t("Something went wrong, please try again."))
      }
    } finally {
      setLoading(false)
    }
  }

  let submitText
  if (promoCode) {
    submitText = t("Sign in and redeem code")
  } else if (gympassToken) {
    submitText = t("Sign in and activate Wellhub")
  } else if (fitnessOnDemandToken) {
    submitText = t("Sign in and link Fitness On Demand account")
  } else if (promoLinkToken) {
    submitText = t("Sign in and get Motosumo Premium")
  }

  return (
    <>
      {apiError !== undefined && <p className="App-api-error">{apiError}</p>}
      <p className="App-description">
        {t("Please enter the password of your\nMotosumo account.")}
      </p>

      <form
        onSubmit={(event) => {
          event.preventDefault()
          loginAndRedeem()
        }}
      >
        <input
          className="App-input password"
          type="password"
          value={password}
          placeholder={t("Password")}
          onChange={(event) => {
            setPassword(event.target.value)
            setInputError(undefined)
          }}
        />
        {inputError !== undefined && (
          <p className="App-input-error">{inputError}</p>
        )}
        {loading ? (
          <div className="loader" />
        ) : (
          <input
            className="App-button"
            type="submit"
            value={submitText}
            disabled={loading}
          />
        )}
      </form>
    </>
  )
}
